import { useCallback, useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useAuth, useReverification, useSignIn, useUser } from "@clerk/clerk-react";
import {
  InvitationCodeCard,
  InvitationStartCard,
  InviteProblem,
  PasswordCard,
  SignedInSetupConflict,
  StatusCard,
} from "../components/AccountCreationScreens.js";
import {
  apiErrorCode,
  clerkErrorCode,
  invitationLookupProblem,
  invitationPasswordUpdateRequest,
  passwordSetupProblem,
  passwordValidationProblem,
} from "../lib/account-creation.js";
import { useApiClient } from "../lib/api-context.js";
import {
  acceptCapturedInvitation,
  accountSetupSnapshot,
  activateCapturedInvitationSession,
  bindCapturedInvitationSession,
  capturedSetupSessionMatches,
  clearInvitationSecret,
  createCapturedInvitationSession,
  exchangeCapturedInvitationProof,
  markInvitationPasswordSaveAttempted,
  resolveCapturedInvitation,
  restartInvitationCode,
  sendCapturedInvitationCode,
  terminalizeInvitationSecret,
  verifyCapturedInvitationCode,
  type AccountSetupSnapshot,
} from "../lib/invitation-secret.js";

interface FatalProblem {
  title: string;
  body: string;
}

export default function CreateAccountPage(): React.ReactElement {
  const { isLoaded: authLoaded, isSignedIn, sessionId } = useAuth();
  const { isLoaded: userLoaded, user } = useUser();
  const { isLoaded: signInLoaded, signIn, setActive } = useSignIn();
  const api = useApiClient();
  const [snapshot, setSnapshot] = useState<AccountSetupSnapshot>(() => accountSetupSnapshot());
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [fatalProblem, setFatalProblem] = useState<FatalProblem | null>(null);
  const [deliveryUncertain, setDeliveryUncertain] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const resolveStarted = useRef(false);
  const sessionResumeStarted = useRef(false);
  const bindStarted = useRef(false);
  const acceptStarted = useRef(false);

  const refreshSnapshot = useCallback(() => {
    setSnapshot(accountSetupSnapshot());
  }, []);

  const updatePassword = useReverification(async (newPassword: string) => {
    if (!user) throw new Error("User is not ready");
    return user.updatePassword(invitationPasswordUpdateRequest(newPassword));
  });

  const showSetupError = useCallback((error: unknown): void => {
    const code = apiErrorCode(error);
    if (code === "invitation_setup_restart") {
      restartInvitationCode();
      refreshSnapshot();
      setProblem("Send a new code to keep setting up your account.");
      return;
    }
    if (code === "invitation_existing_login") {
      terminalizeInvitationSecret();
      refreshSnapshot();
      setFatalProblem({
        title: "You already have a login",
        body: "Sign in with your existing account. If you still need access to this dashboard, contact Scale Advisors.",
      });
      return;
    }
    if (
      code === "invitation_expired"
      || code === "invitation_revoked"
      || code === "invitation_used"
      || code === "invitation_accepted"
    ) {
      const copy = invitationLookupProblem(code);
      terminalizeInvitationSecret();
      refreshSnapshot();
      setFatalProblem(copy);
      return;
    }
    if (code === "invitation_code_invalid") {
      setProblem("That code did not work. Check it and try again.");
      return;
    }
    if (code === "invitation_rate_limited") {
      setProblem("Too many tries. Wait a while, then try again.");
      return;
    }
    if (code === "identity_maintenance") {
      setProblem("Login maintenance is in progress. Try again in a minute.");
      return;
    }
    if (code === "invitation_setup_pending" || code === "invitation_provider_unavailable") {
      setProblem("Account setup is taking longer than expected. Try again in a moment.");
      return;
    }
    setProblem("We couldn't finish account setup. Try again in a moment.");
  }, [refreshSnapshot]);

  const finishSetup = useCallback(async (): Promise<void> => {
    if (acceptStarted.current) return;
    if (!capturedSetupSessionMatches(sessionId)) {
      setFatalProblem({
        title: "Open your invitation link again",
        body: "Your secure setup session ended before account creation was complete.",
      });
      return;
    }
    acceptStarted.current = true;
    setWorking(true);
    setProblem(null);
    try {
      await acceptCapturedInvitation(api);
      clearInvitationSecret();
      window.location.assign("/dashboard");
    } catch (error) {
      showSetupError(error);
      setPasswordSaved(user?.passwordEnabled === true || accountSetupSnapshot().passwordSaveAttempted);
      setWorking(false);
      acceptStarted.current = false;
    }
  }, [api, sessionId, showSetupError, user?.passwordEnabled]);

  useEffect(() => {
    if (
      !authLoaded
      || isSignedIn === true
      || snapshot.phase !== "captured"
      || resolveStarted.current
    ) return;
    resolveStarted.current = true;
    setWorking(true);
    void resolveCapturedInvitation(api)
      .then(() => {
        refreshSnapshot();
        setWorking(false);
      })
      .catch((error: unknown) => {
        const code = apiErrorCode(error);
        if (code) {
          const copy = invitationLookupProblem(code);
          setFatalProblem(copy);
        } else {
          setFatalProblem({
            title: "We couldn't check this invitation",
            body: "Try this link again in a minute.",
          });
        }
        setWorking(false);
      });
  }, [api, authLoaded, isSignedIn, refreshSnapshot, snapshot.phase]);

  useEffect(() => {
    if (
      !authLoaded
      || isSignedIn === true
      || !signInLoaded
      || !signIn
      || !setActive
      || sessionResumeStarted.current
      || (snapshot.phase !== "ticket_issued"
        && snapshot.phase !== "ticket_created"
        && snapshot.phase !== "session_active")
    ) return;
    sessionResumeStarted.current = true;
    setWorking(true);
    setProblem(null);
    void (async () => {
      try {
        if (accountSetupSnapshot().phase === "ticket_issued") {
          await createCapturedInvitationSession(signIn);
          refreshSnapshot();
        }
        await activateCapturedInvitationSession(setActive);
        refreshSnapshot();
      } catch (error) {
        const code = clerkErrorCode(error);
        setProblem(
          code === "ticket_expired" || code === "verification_expired"
            ? "Your secure sign-in expired. Ask for a new invitation link."
            : "We couldn't start your secure setup session. Ask for a new invitation link.",
        );
        setWorking(false);
        sessionResumeStarted.current = false;
      }
    })();
  }, [authLoaded, isSignedIn, refreshSnapshot, setActive, signIn, signInLoaded, snapshot.phase]);

  useEffect(() => {
    if (
      !authLoaded
      || isSignedIn !== true
      || !userLoaded
      || !capturedSetupSessionMatches(sessionId)
      || bindStarted.current
      || (snapshot.phase !== "ticket_created" && snapshot.phase !== "session_active")
    ) return;
    bindStarted.current = true;
    setWorking(true);
    setProblem(null);
    void (async () => {
      try {
        if (accountSetupSnapshot().phase === "ticket_created" && setActive) {
          await activateCapturedInvitationSession(setActive);
        }
        await bindCapturedInvitationSession(api);
        refreshSnapshot();
        setWorking(false);
      } catch (error) {
        showSetupError(error);
        setWorking(false);
        bindStarted.current = false;
      }
    })();
  }, [api, authLoaded, isSignedIn, refreshSnapshot, sessionId, setActive, showSetupError, snapshot.phase, userLoaded]);

  useEffect(() => {
    if (
      snapshot.phase !== "bound"
      || isSignedIn !== true
      || !userLoaded
      || !snapshot.passwordSaveAttempted
      || user?.passwordEnabled !== true
    ) return;
    setPasswordSaved(true);
    void finishSetup();
  }, [finishSetup, isSignedIn, snapshot.passwordSaveAttempted, snapshot.phase, user?.passwordEnabled, userLoaded]);

  const sendCode = async (): Promise<void> => {
    setWorking(true);
    setProblem(null);
    try {
      const sent = await sendCapturedInvitationCode(api);
      setDeliveryUncertain(!sent.deliveryConfirmed);
      refreshSnapshot();
    } catch (error) {
      showSetupError(error);
    } finally {
      setWorking(false);
    }
  };

  const submitCode = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const form = event.currentTarget;
    const codeValue = new FormData(form).get("code");
    const code = typeof codeValue === "string" ? codeValue.trim() : "";
    form.reset();
    if (!/^\d{6}$/.test(code)) {
      setProblem("Enter the 6-digit code from your email.");
      return;
    }
    if (!signInLoaded || !signIn || !setActive) return;
    setWorking(true);
    setProblem(null);
    try {
      await verifyCapturedInvitationCode(api, code);
      refreshSnapshot();
      await exchangeCapturedInvitationProof(api);
      refreshSnapshot();
      await createCapturedInvitationSession(signIn);
      refreshSnapshot();
      await activateCapturedInvitationSession(setActive);
      refreshSnapshot();
    } catch (error) {
      const clerkCode = clerkErrorCode(error);
      if (clerkCode) {
        setProblem("We couldn't start your secure setup session. Ask for a new invitation link.");
      } else {
        showSetupError(error);
      }
      setWorking(false);
    }
  };

  const submitPassword = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const passwordValue = formData.get("password");
    const passwordAgainValue = formData.get("passwordAgain");
    const password = typeof passwordValue === "string" ? passwordValue : "";
    const passwordAgain = typeof passwordAgainValue === "string" ? passwordAgainValue : "";
    const validationProblem = passwordValidationProblem(password, passwordAgain);
    if (validationProblem !== null) {
      form.reset();
      setProblem(validationProblem);
      return;
    }
    setWorking(true);
    setProblem(null);
    markInvitationPasswordSaveAttempted();
    refreshSnapshot();
    try {
      await updatePassword(password);
      form.reset();
      await user?.reload();
      if (user?.passwordEnabled !== true) throw new Error("Password was not confirmed");
      setPasswordSaved(true);
      await finishSetup();
    } catch (error) {
      form.reset();
      try {
        await user?.reload();
      } catch {
        // The retry button below rechecks through the authenticated server path.
      }
      if (user?.passwordEnabled === true) {
        setPasswordSaved(true);
        await finishSetup();
        return;
      }
      setProblem(passwordSetupProblem(clerkErrorCode(error)));
      setWorking(false);
    }
  };

  if (!authLoaded || !userLoaded) return <StatusCard text="Loading your invitation..." />;
  if (fatalProblem) return <InviteProblem title={fatalProblem.title} body={fatalProblem.body} />;
  if (snapshot.phase === "empty" || snapshot.phase === "terminal") {
    return (
      <InviteProblem
        title="You need an invitation"
        body="Ask your Scale Advisors contact for a new personal invitation link."
      />
    );
  }

  const setupSession = capturedSetupSessionMatches(sessionId);
  if (isSignedIn === true && !setupSession) {
    if (snapshot.phase === "bound" || snapshot.phase === "accepted") {
      return (
        <InviteProblem
          title="Your secure setup session ended"
          body="Contact Scale Advisors so we can safely finish your account."
        />
      );
    }
    return (
      <SignedInSetupConflict
        email={user?.primaryEmailAddress?.emailAddress ?? "your current account"}
      />
    );
  }

  if (snapshot.phase === "captured") return <StatusCard text="Checking your invitation..." />;
  if (snapshot.phase === "resolved") {
    return (
      <InvitationStartCard
        accountName={snapshot.accountName ?? "your dashboard"}
        emailHint={snapshot.emailHint ?? "the invited email"}
        working={working}
        problem={problem}
        onSendCode={() => { void sendCode(); }}
      />
    );
  }
  if (snapshot.phase === "code_sent") {
    return (
      <InvitationCodeCard
        accountName={snapshot.accountName ?? "your dashboard"}
        emailHint={snapshot.emailHint ?? "the invited email"}
        working={working}
        problem={problem}
        deliveryUncertain={deliveryUncertain}
        onSubmit={(event) => { void submitCode(event); }}
        onResend={() => { void sendCode(); }}
      />
    );
  }
  if (
    snapshot.phase === "code_verified"
    || snapshot.phase === "ticket_issued"
    || snapshot.phase === "ticket_created"
    || snapshot.phase === "session_active"
  ) {
    if (problem) {
      return <InviteProblem title="We couldn't finish setup" body={problem} />;
    }
    return <StatusCard text="Securing your account..." />;
  }
  if (snapshot.phase === "accepted") return <StatusCard text="Opening your dashboard..." />;

  if (snapshot.phase === "bound" && setupSession) {
    if (user?.passwordEnabled && !snapshot.passwordSaveAttempted) {
      return (
        <InviteProblem
          title="Contact Scale Advisors"
          body="This login changed before account setup finished. We need to verify it before continuing."
        />
      );
    }
    return (
      <PasswordCard
        accountName={snapshot.accountName ?? "your dashboard"}
        email={user?.primaryEmailAddress?.emailAddress ?? "Your invited email"}
        working={working}
        problem={problem}
        passwordSaved={passwordSaved}
        onSubmit={(event) => { void submitPassword(event); }}
        onRetryFinish={() => { void finishSetup(); }}
      />
    );
  }

  return <StatusCard text="Finishing account setup..." />;
}
